import React from 'react';
import { t } from '../i18n';
import Modal from './ui/Modal';
import { useCurrency } from '../utils/currencyContext';
import { calculateSettlements } from '../utils/pokerLogic';
import { settlementEntries } from '../utils/playerLedger';
import { formatSignedCurrency } from '../utils/settlementRecap';

/**
 * Mid-game peek at who owes whom. Read-only — does not end the game or write Firebase.
 *
 * Uses stack + totalPotContribution so chips still in the current hand are attributed
 * back to their contributors (fair running balance while a pot is live).
 */
export default function BalancesModal({ open, onClose, players = {} }) {
  const { formatCurrency, currency, locale } = useCurrency();
  // Avoid settlement math (and its console logging) while the modal is closed.
  if (!open) return null;

  const playersArray = Object.values(players || {}).sort(
    (a, b) => (a.joinedAt || 0) - (b.joinedAt || 0)
  );

  const potInPlay = playersArray.some((p) => (p.totalPotContribution || 0) > 0);

  const playersForSettlement = settlementEntries(players).map((player) => ({
    ...player,
    // Attribute chips still in this hand back to the contributor for a running total,
    // including a player who cashed out after putting money in the pot this hand.
    stack: (player.status === 'left' ? (player.cashedOut ?? player.stack ?? 0) : (player.stack || 0))
      + (player.totalPotContribution || 0),
    totalInvestment: player.totalInvestment || 0,
  }));

  const { transactions, playersWithResults, hasSettlements } =
    calculateSettlements(playersForSettlement);

  const sorted = [...playersWithResults].sort(
    (a, b) => (b.netResult || 0) - (a.netResult || 0)
  );

  return (
    <Modal open={open} onClose={onClose} title={t('balances.title')}>
      <p className="text-[13px] text-mute mb-4">
        {t('balances.intro')}
      </p>

      {potInPlay && (
        <p className="text-[12px] text-mute mb-4 px-3 py-2 rounded-xl border border-line bg-cream-2">
          {t('balances.includesPot')}
        </p>
      )}

      <div className="label-eyebrow text-mute">{t('settle.whoOwes')}</div>
      <div className="mt-2 mb-5">
        {!hasSettlements ? (
          <div className="py-3.5 border-t border-b border-line text-[15px] text-mute">
              {t('balances.even')}
          </div>
        ) : (
          transactions.map((tx, i) => (
            <div
              key={`${tx.fromPlayerId}-${tx.toPlayerId}-${i}`}
              className="flex items-center justify-between py-3.5"
              style={{
                borderTop: i === 0 ? '1px solid #dfdbd3' : 'none',
                borderBottom: '1px solid #dfdbd3',
              }}
            >
              <div className="text-[15px] tracking-[-0.01em]">
                <span className="font-semibold">{tx.from}</span>
                <span className="text-mute mx-1.5">→</span>
                <span className="font-semibold">{tx.to}</span>
              </div>
              <div className="text-[16px] font-semibold tnum tracking-[-0.015em]">
                {formatCurrency(tx.amount)}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="label-eyebrow text-mute">{t('balances.net')}</div>
      <div className="mt-2 flex flex-col gap-1.5 mb-5">
        {sorted.map((player) => {
          const net = player.netResult || 0;
          return (
            <div
              key={player.id}
              className="flex items-center justify-between px-3.5 py-2.5 rounded-xl border border-line"
            >
              <div className="min-w-0">
                <span className="text-[14.5px] font-semibold tracking-[-0.01em]">{player.name}</span>
              </div>
              <span
                className={`text-[15px] font-semibold tnum tracking-[-0.015em] ${
                  net < 0 ? 'text-mute' : 'text-ink'
                }`}
              >
                {formatSignedCurrency(net, currency, locale)}
              </span>
            </div>
          );
        })}
      </div>

      <button
        onClick={onClose}
        className="w-full h-12 rounded-[12px] bg-ink text-cream text-[14px] font-semibold"
      >
        {t('balances.keepPlaying')}
      </button>
    </Modal>
  );
}
